import React from 'react';
import { useHistory } from 'react-router';

// Type
import { GoalItem } from 'Shared/constants/types';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Constants
import PageUrlsConstants from 'Shared/constants/PageUrls.constants';

// Hooks
import { useGoal } from 'Shared/utils/hooks/use-goal';

// Components
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';
import { WpAlert } from 'Wpds/components/alert';

type Props = {
  goal: GoalItem;
  isOpen: boolean;
  toggleIsOpen: () => void;
  onDeleteSuccess?: Function;
};

const GoalDeleteConfirmationModal = ({ goal, isOpen, toggleIsOpen, onDeleteSuccess }: Props) => {
  const { tr } = useTranslation();
  const history = useHistory();
  const { isSaving, handleDeleteGoal, subGoals, parentGoal } = useGoal({ slug: goal.slug });

  const hasSubGoals = subGoals.length > 0;
  const totalActionItems = goal.numCompleteActionItems + goal.numIncompleteActionItems;
  const hasActionItems = totalActionItems > 0;

  const handleConfirmClick = () => {
    handleDeleteGoal().then(() => {
      if (onDeleteSuccess) onDeleteSuccess();
      else history.push(PageUrlsConstants.GOALS_PAGE);
      toggleIsOpen();
    });
  };

  return (
    <ConfirmModal
      message="Are you sure you want to delete this goal?"
      isOpen={isOpen}
      isProcessing={isSaving}
      onToggle={toggleIsOpen}
      onConfirmClick={handleConfirmClick}
      confirmButtonLabel="Delete"
    >
      <p>Are you sure you want to delete this goal?</p>
      {(hasSubGoals || hasActionItems) && (
        <WpAlert showIcon={false} type="note">
          <p style={{ fontWeight: 'bold' }}>{tr('perform.goals.delete_modal.quick_note')}</p>
          <ul>
            {hasSubGoals && parentGoal && (
              <li>
                {subGoals.length === 1
                  ? tr(
                      'perform.goals.delete_modal.sub_goals_with_parent_singular',
                      subGoals.length.toString(),
                      parentGoal.name
                    )
                  : tr(
                      'perform.goals.delete_modal.sub_goals_with_parent_plural',
                      subGoals.length.toString(),
                      parentGoal.name
                    )}
              </li>
            )}
            {hasSubGoals && !parentGoal && (
              <li>
                {subGoals.length === 1
                  ? tr('perform.goals.delete_modal.sub_goals_without_parent_singular', subGoals.length.toString())
                  : tr('perform.goals.delete_modal.sub_goals_without_parent_plural', subGoals.length.toString())}
              </li>
            )}
            {hasActionItems && (
              <li>
                {totalActionItems === 1
                  ? tr('perform.goals.delete_modal.action_items_warning_singular', totalActionItems.toString())
                  : tr('perform.goals.delete_modal.action_items_warning_plural', totalActionItems.toString())}
              </li>
            )}
          </ul>
        </WpAlert>
      )}
    </ConfirmModal>
  );
};

export default GoalDeleteConfirmationModal;
